import type { IEditorModel, IPlayerModel, IContentMetadata } from '@lumieducation/h5p-server';

export interface IContentListEntry {
    contentId: string;
    mainLibrary: string;
    title: string;
    originalNewKey?: string;
}

export interface IContentService {
    delete(contentId: string): Promise<void>;
    getEdit(contentId: string, library?: string): Promise<IEditorModel>;
    getPlay(
        contentId: string,
        contextId?: string,
        asUserId?: string,
        readOnlyState?: boolean
    ): Promise<IPlayerModel>;
    list(): Promise<IContentListEntry[]>;
    save(
        contentId: string,
        requestBody: { library: string; params: any }
    ): Promise<{ contentId: string; metadata: IContentMetadata }>;
    generateDownloadLink(contentId: string): string;
    generateUploadLink(contentId: string): string;
}

export class ContentService implements IContentService {
    /**
     *
     */
    constructor(protected baseUrl: string = '') { }

    private csrfToken: string | undefined = undefined;

    delete = async (contentId: string): Promise<void> => {
        console.log(`ContentService: deleting ${contentId}...`);
        const result = await fetch(`${this.baseUrl}/${contentId}`, {
            method: 'delete',
            headers: {
                'CSRF-Token': this.csrfToken ?? ''
            }
        });
        if (!result.ok) {
            throw new Error(
                `Error while deleting content: ${result.status} ${result.statusText
                } ${await result.text()}`
            );
        }
    };

    getEdit = async (contentId: string, library?: string): Promise<IEditorModel> => {
        console.log(`ContentService: Getting information to edit ${contentId}...`);
        if (library) {
            console.log(`ContentService: Library parameter: ${library}`);
        }

        // Build URL with optional library query parameter
        let url = `${this.baseUrl}/${contentId === 'new' ? 'new' : contentId}/edit`;
        if (contentId === 'new' && library) {
            url += `?library=${encodeURIComponent(library)}`;
            console.log(`ContentService: Request URL: ${url}`);
        }

        const res = await fetch(url);
        if (!res || !res.ok) {
            const errorText = await res.text();
            console.error(`ContentService: Error response: ${res.status} ${res.statusText} - ${errorText}`);
            throw new Error(`${res.status} ${res.statusText}: ${errorText}`);
        }
        return res.json();
    };

    getPlay = async (
        contentId: string,
        contextId?: string,
        asUserId?: string,
        readOnlyState?: boolean
    ): Promise<IPlayerModel> => {
        console.log(
            `ContentService: Getting information to play ${contentId}${contextId ? `, contextId ${contextId}` : ''
            }${asUserId ? `, asUserId ${asUserId}` : ''}${readOnlyState !== undefined ? `, readOnlyState ${readOnlyState}` : ''
            }...`
        );

        const query = new URLSearchParams();
        if (contextId) {
            query.append('contextId', contextId);
        }
        if (asUserId) {
            query.append('asUserId', asUserId);
        }
        if (readOnlyState === true) {
            query.append('readOnlyState', 'yes');
        }

        const queryString = query.toString();
        const res = await fetch(
            `${this.baseUrl}/${contentId}/play${queryString ? `?${queryString}` : ''}`
        );
        if (!res || !res.ok) {
            throw new Error(`${res.status} ${res.statusText}`);
        }
        return res.json();
    };

    list = async (): Promise<IContentListEntry[]> => {
        console.log(`ContentService: Listing content objects`);
        try {
            const result = await fetch(this.baseUrl);
            if (result.ok) {
                const data = await result.json();
                console.log('Content list fetched successfully:', data);
                return data;
            } else {
                console.warn(
                    `Request failed with status ${result.status} ${result.statusText}: ${await result.text()}`
                );
                // Return an empty array if the request fails
                return [];
            }
        } catch (error) {
            console.error('An error occurred while fetching content:', error);
            // Return an empty array in case of an error
            return [];
        }
    };

    save = async (
        contentId: string,
        requestBody: { library: string; params: any }
    ): Promise<{ contentId: string; metadata: IContentMetadata }> => {
        console.log('callback ContentService save');
        if (contentId) {
            console.log(`ContentService: Saving new content.`);
        } else {
            console.log(`ContentService: Saving content ${contentId}`);
        }

        const body = JSON.stringify(requestBody);

        const res = contentId
            ? await fetch(`${this.baseUrl}/${contentId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'CSRF-Token': this.csrfToken ?? ''
                },
                body
            })
            : await fetch(this.baseUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'CSRF-Token': this.csrfToken ?? ''
                },
                body
            });

        if (!res || !res.ok) {
            throw new Error(`${res.status} ${res.statusText} - ${await res.text()}`);
        }
        return res.json();
    };
    generateDownloadLink = (contentId: string): string => `${this.baseUrl}/download/${contentId}`;

    generateUploadLink = (contentId: string): string => `${this.baseUrl}/upload/${contentId}`;

    setCsrfToken = (csrfToken): void => {
        this.csrfToken = csrfToken;
    };
    getCsrfToken = (): string | undefined => {
        return this.csrfToken;
    };
}
